import * as React from 'react';
import {
  getFirstEnabledOptionIndex,
  getLastEnabledOptionIndex,
  getNextEnabledOptionIndex,
  getOptionIndex,
} from './select.utils';
import {type SelectValue} from './types';
import {type SelectOption} from '../../lib/selector-option';
import type {ReactNode} from 'react';

type UseDesktopSelectParams<
  T,
  V extends SelectValue,
  L extends string | number | ReactNode,
> = {
  isMobile: boolean;
  isOpen: boolean;
  options: SelectOption<T, V, L>[];
  value?: SelectOption<T, V, L> | null;
  disabled?: boolean;
  onChange?: (option: SelectOption<T, V, L>) => void;
};

function useDesktopSelect<
  T,
  V extends SelectValue,
  L extends string | number | ReactNode,
>({
  isMobile,
  isOpen,
  options,
  value,
  disabled,
  onChange,
}: UseDesktopSelectParams<T, V, L>) {
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = getOptionIndex(options, value);

  React.useEffect(() => {
    if (!isOpen || isMobile) return;

    setHighlightedIndex(currentIndex => {
      if (currentIndex >= 0) {
        return currentIndex;
      }

      return selectedIndex >= 0 ? selectedIndex : getFirstEnabledOptionIndex(options);
    });

    const frameId = window.requestAnimationFrame(() => {
      listRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isMobile, isOpen, options, selectedIndex]);

  React.useEffect(() => {
    if (!isOpen || isMobile || highlightedIndex < 0) return;

    optionRefs.current[highlightedIndex]?.scrollIntoView({
      block: 'nearest',
    });
  }, [highlightedIndex, isMobile, isOpen]);

  React.useEffect(() => {
    if (isOpen) return;

    setHighlightedIndex(-1);
  }, [isOpen]);

  const focusTrigger = React.useCallback(() => {
    triggerRef.current?.focus();
  }, []);

  const handleSelect = React.useCallback(
    (option: SelectOption<T, V, L>) => {
      if (option.isDisabled || disabled) return;

      onChange?.(option);
    },
    [disabled, onChange],
  );

  const openMenu = React.useCallback(
    (targetIndex?: number) => {
      const fallbackIndex =
        selectedIndex >= 0 ? selectedIndex : getFirstEnabledOptionIndex(options);
      setHighlightedIndex(targetIndex ?? fallbackIndex);
    },
    [options, selectedIndex],
  );

  const handleTriggerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, onOpen: () => void) => {
      if (disabled) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const startIndex =
          selectedIndex >= 0 ? selectedIndex + 1 : getFirstEnabledOptionIndex(options);
        openMenu(getNextEnabledOptionIndex(options, startIndex, 1));
        onOpen();
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const startIndex =
          selectedIndex >= 0 ? selectedIndex - 1 : getLastEnabledOptionIndex(options);
        openMenu(getNextEnabledOptionIndex(options, startIndex, -1));
        onOpen();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openMenu();
        onOpen();
      }
    },
    [disabled, openMenu, options, selectedIndex],
  );

  const handleMenuKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, onClose: () => void) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        focusTrigger();
        return;
      }

      if (event.key === 'Tab') {
        onClose();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = getNextEnabledOptionIndex(options, highlightedIndex + 1, 1);
        if (nextIndex >= 0) {
          setHighlightedIndex(nextIndex);
        }
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const nextIndex = getNextEnabledOptionIndex(options, highlightedIndex - 1, -1);
        if (nextIndex >= 0) {
          setHighlightedIndex(nextIndex);
        }
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        setHighlightedIndex(getFirstEnabledOptionIndex(options));
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        setHighlightedIndex(getLastEnabledOptionIndex(options));
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const option = options[highlightedIndex];

        if (option && !option.isDisabled) {
          onChange?.(option);
          onClose();
          focusTrigger();
        }
      }
    },
    [focusTrigger, highlightedIndex, onChange, options],
  );

  const setOptionRef = React.useCallback(
    (index: number, node: HTMLButtonElement | null) => {
      optionRefs.current[index] = node;
    },
    [],
  );

  return {
    highlightedIndex,
    listRef,
    setHighlightedIndex,
    setOptionRef,
    triggerRef,
    focusTrigger,
    handleSelect,
    handleMenuKeyDown,
    handleTriggerKeyDown,
  };
}

export {useDesktopSelect};
